import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { TablerIcon } from "@/components/icons/TablerIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const CUSTOM_UNITS = ["m2", "mb", "szt.", "kpl.", "godz."] as const;
type CustomUnit = (typeof CUSTOM_UNITS)[number];

type ExtendedDatabase = Omit<Database, "public"> & {
  public: Omit<Database["public"], "Tables"> & {
    Tables: Database["public"]["Tables"] & {
      work_templates: {
        Row: {
          id: string;
          name: string;
          category: string | null;
          unit: string;
          default_labor_cost: number | null;
          default_material_cost: number | null;
          generator_key: string | null;
          is_custom: boolean;
          created_by: string | null;
        };
        Insert: {
          id?: string;
          name: string;
          category?: string | null;
          unit: string;
          default_labor_cost?: number | null;
          default_material_cost?: number | null;
          generator_key?: string | null;
          is_custom?: boolean;
          created_by?: string | null;
        };
        Update: {
          id?: string;
          name?: string;
          category?: string | null;
          unit?: string;
          default_labor_cost?: number | null;
          default_material_cost?: number | null;
          generator_key?: string | null;
          is_custom?: boolean;
          created_by?: string | null;
        };
        Relationships: [];
      };
      user_rates: {
        Row: {
          id: string;
          user_id: string;
          work_template_id: string;
          labor_cost_per_unit: number;
          material_cost_per_unit: number;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          work_template_id: string;
          labor_cost_per_unit?: number;
          material_cost_per_unit?: number;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          work_template_id?: string;
          labor_cost_per_unit?: number;
          material_cost_per_unit?: number;
          updated_at?: string;
        };
        Relationships: [];
      };
    };
  };
};

type WorkTemplateRow = ExtendedDatabase["public"]["Tables"]["work_templates"]["Row"];
type UserRateRow = ExtendedDatabase["public"]["Tables"]["user_rates"]["Row"];

type RateInput = {
  labor: string;
  material: string;
};

const supabaseWithRates = supabase as unknown as SupabaseClient<ExtendedDatabase>;

function parseMoneyInput(raw: string): number | null {
  const normalized = raw.trim().replace(",", ".");
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
}

function formatMoneyInput(value: number): string {
  return value.toFixed(2);
}

export default function MojeStawki() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [templates, setTemplates] = useState<WorkTemplateRow[]>([]);
  const [values, setValues] = useState<Record<string, RateInput>>({});
  const [dirtyRows, setDirtyRows] = useState<Record<string, boolean>>({});
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | "all">("all");
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [creatingCustom, setCreatingCustom] = useState(false);
  const [deletingCustomId, setDeletingCustomId] = useState<string | null>(null);
  const [customForm, setCustomForm] = useState<{
    name: string;
    unit: CustomUnit;
    labor: string;
    material: string;
  }>({
    name: "",
    unit: "szt.",
    labor: "",
    material: "",
  });

  const templatesById = useMemo(() => {
    return new Map<string, WorkTemplateRow>(templates.map((t) => [t.id, t]));
  }, [templates]);

  const generatorTemplates = useMemo(
    () => templates.filter((t) => t.generator_key != null),
    [templates],
  );

  const customTemplates = useMemo(
    () => templates.filter((t) => t.is_custom === true),
    [templates],
  );

  const groupedTemplates = useMemo(() => {
    const grouped = new Map<string, WorkTemplateRow[]>();
    for (const template of generatorTemplates) {
      const category = template.category?.trim() || "Inne";
      const bucket = grouped.get(category) ?? [];
      bucket.push(template);
      grouped.set(category, bucket);
    }
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b, "pl-PL"));
  }, [generatorTemplates]);

  const sortedCustomTemplates = useMemo(
    () => [...customTemplates].sort((a, b) => a.name.localeCompare(b.name, "pl-PL")),
    [customTemplates],
  );

  const categoryChips = useMemo(
    () => groupedTemplates.map(([category]) => category),
    [groupedTemplates],
  );

  const filteredGroupedTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return groupedTemplates
      .filter(([category]) => activeCategory === "all" || category === activeCategory)
      .map(([category, rows]) => {
        const filteredRows = q
          ? rows.filter((t) => t.name.toLowerCase().includes(q) || category.toLowerCase().includes(q))
          : rows;
        return [category, filteredRows] as const;
      })
      .filter(([, rows]) => rows.length > 0);
  }, [groupedTemplates, searchQuery, activeCategory]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);

      const [generatorTemplatesRes, customTemplatesRes, ratesRes] = await Promise.all([
        supabaseWithRates
          .from("work_templates")
          .select("id, name, category, unit, default_labor_cost, default_material_cost, generator_key, is_custom, created_by")
          .not("generator_key", "is", null)
          .order("category", { ascending: true })
          .order("name", { ascending: true }),
        supabaseWithRates
          .from("work_templates")
          .select("id, name, category, unit, default_labor_cost, default_material_cost, generator_key, is_custom, created_by")
          .eq("is_custom", true)
          .eq("created_by", userId)
          .order("name", { ascending: true }),
        supabaseWithRates
          .from("user_rates")
          .select("id, user_id, work_template_id, labor_cost_per_unit, material_cost_per_unit, updated_at")
          .eq("user_id", userId),
      ]);

      if (cancelled) return;

      if (generatorTemplatesRes.error || customTemplatesRes.error || ratesRes.error) {
        toast({
          title: "Nie udało się wczytać stawek",
          description:
            generatorTemplatesRes.error?.message ??
            customTemplatesRes.error?.message ??
            ratesRes.error?.message ??
            "Spróbuj ponownie.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const templatesData = [
        ...(generatorTemplatesRes.data ?? []),
        ...(customTemplatesRes.data ?? []),
      ];
      const ratesData = ratesRes.data ?? [];
      const ratesByTemplate = new Map<string, UserRateRow>(
        ratesData.map((r) => [r.work_template_id, r]),
      );

      const nextValues: Record<string, RateInput> = {};
      for (const template of templatesData) {
        const rate = ratesByTemplate.get(template.id);
        nextValues[template.id] = {
          labor: rate ? formatMoneyInput(rate.labor_cost_per_unit) : "",
          material: rate ? formatMoneyInput(rate.material_cost_per_unit) : "",
        };
      }

      setTemplates(templatesData);
      setValues(nextValues);
      setDirtyRows({});
      setSavingRows({});
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [toast, user?.id]);

  const setFieldValue = (templateId: string, field: keyof RateInput, value: string) => {
    setValues((prev) => ({
      ...prev,
      [templateId]: {
        labor: prev[templateId]?.labor ?? "",
        material: prev[templateId]?.material ?? "",
        [field]: value,
      },
    }));
    setDirtyRows((prev) => ({ ...prev, [templateId]: true }));
  };

  const saveTemplateRate = async (templateId: string): Promise<boolean> => {
    const userId = user?.id;
    const template = templatesById.get(templateId);
    if (!userId || !template) return false;

    const raw = values[templateId] ?? { labor: "", material: "" };
    const laborCost = parseMoneyInput(raw.labor) ?? Number(template.default_labor_cost ?? 0);
    const materialCost = parseMoneyInput(raw.material) ?? Number(template.default_material_cost ?? 0);

    setSavingRows((prev) => ({ ...prev, [templateId]: true }));
    const { error } = await supabaseWithRates.from("user_rates").upsert(
      {
        user_id: userId,
        work_template_id: templateId,
        labor_cost_per_unit: laborCost,
        material_cost_per_unit: materialCost,
      },
      { onConflict: "user_id,work_template_id" },
    );
    setSavingRows((prev) => ({ ...prev, [templateId]: false }));

    if (error) {
      toast({
        title: "Błąd zapisu",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    setValues((prev) => ({
      ...prev,
      [templateId]: {
        labor: formatMoneyInput(laborCost),
        material: formatMoneyInput(materialCost),
      },
    }));
    setDirtyRows((prev) => ({ ...prev, [templateId]: false }));
    return true;
  };

  const handleCreateCustomPosition = async () => {
    const userId = user?.id;
    if (!userId) return;

    const name = customForm.name.trim();
    const labor = parseMoneyInput(customForm.labor);
    const material = parseMoneyInput(customForm.material);
    if (!name) {
      toast({ title: "Podaj nazwę pozycji", variant: "destructive" });
      return;
    }
    if (labor == null || material == null) {
      toast({ title: "Podaj poprawne stawki", description: "Stawki muszą być >= 0.", variant: "destructive" });
      return;
    }

    setCreatingCustom(true);
    const { data: insertedTemplate, error: insertError } = await supabaseWithRates
      .from("work_templates")
      .insert({
        name,
        unit: customForm.unit,
        category: "Własne",
        default_labor_cost: labor,
        default_material_cost: material,
        generator_key: null,
        is_custom: true,
        created_by: userId,
      })
      .select("id, name, category, unit, default_labor_cost, default_material_cost, generator_key, is_custom, created_by")
      .single();

    if (insertError || !insertedTemplate) {
      setCreatingCustom(false);
      toast({ title: "Nie udało się dodać pozycji", description: insertError?.message ?? "Spróbuj ponownie.", variant: "destructive" });
      return;
    }

    const { error: rateError } = await supabaseWithRates.from("user_rates").upsert(
      {
        user_id: userId,
        work_template_id: insertedTemplate.id,
        labor_cost_per_unit: labor,
        material_cost_per_unit: material,
      },
      { onConflict: "user_id,work_template_id" },
    );

    setCreatingCustom(false);
    if (rateError) {
      toast({ title: "Pozycja dodana, ale zapis stawek nie powiódł się", description: rateError.message, variant: "destructive" });
      return;
    }

    setTemplates((prev) => [...prev, insertedTemplate]);
    setValues((prev) => ({
      ...prev,
      [insertedTemplate.id]: {
        labor: formatMoneyInput(labor),
        material: formatMoneyInput(material),
      },
    }));
    setDirtyRows((prev) => ({ ...prev, [insertedTemplate.id]: false }));
    setCustomForm({ name: "", unit: "szt.", labor: "", material: "" });
    toast({ title: "Dodano własną pozycję" });
  };

  const handleDeleteCustomPosition = async (templateId: string) => {
    const userId = user?.id;
    if (!userId) return;

    setDeletingCustomId(templateId);
    const { error } = await supabaseWithRates
      .from("work_templates")
      .delete()
      .eq("id", templateId)
      .eq("is_custom", true)
      .eq("created_by", userId);
    setDeletingCustomId(null);

    if (error) {
      toast({ title: "Nie udało się usunąć pozycji", description: error.message, variant: "destructive" });
      return;
    }

    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    setValues((prev) => {
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
    setDirtyRows((prev) => {
      const next = { ...prev };
      delete next[templateId];
      return next;
    });
    toast({ title: "Usunięto własną pozycję" });
  };

  const handleSaveAll = async () => {
    const ids = Object.entries(dirtyRows)
      .filter(([, isDirty]) => isDirty)
      .map(([id]) => id);
    if (ids.length === 0) {
      toast({ title: "Brak zmian do zapisu" });
      return;
    }

    setSavingAll(true);
    let successCount = 0;
    for (const id of ids) {
      const ok = await saveTemplateRate(id);
      if (ok) successCount += 1;
    }
    setSavingAll(false);

    if (successCount === ids.length) {
      toast({ title: "Zapisano wszystkie stawki" });
      return;
    }

    toast({
      title: "Część stawek nie zapisała się",
      description: `Zapisano ${successCount}/${ids.length}.`,
      variant: "destructive",
    });
  };

  return (
    <div className="workshop-modern min-h-screen">
      <header className="border-b border-[var(--line)] bg-[var(--paper)] px-5 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <Link to="/dashboard" className="text-sm font-medium text-[var(--ink-3)] hover:text-[var(--ink)]">
            ← Panel
          </Link>
          <Button
            type="button"
            className="dashboard-btn-primary border-0"
            onClick={() => void handleSaveAll()}
            disabled={savingAll || loading}
          >
            {savingAll ? "Zapisuję..." : "Zapisz wszystko"}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-[var(--ink)]">Moje stawki</h1>
          <p className="mt-1 text-sm text-[var(--ink-3)]">
            Własne stawki robocizny i materiału dla szablonów prac.
          </p>
        </div>

        <div className="rates-search-bar">
          <TablerIcon name="search" size={18} className="text-[var(--ink-4)]" />
          <input
            type="search"
            placeholder="Szukaj pozycji…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Szukaj pozycji"
          />
        </div>

        {categoryChips.length > 0 && (
          <div className="rates-chip-row">
            <button
              type="button"
              className={cn("rates-chip", activeCategory === "all" && "is-active")}
              onClick={() => setActiveCategory("all")}
            >
              Wszystkie
            </button>
            {categoryChips.map((category) => (
              <button
                key={category}
                type="button"
                className={cn("rates-chip", activeCategory === category && "is-active")}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}

      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">ŝadowanie stawek...</div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-border/70 bg-card px-4 py-8 text-sm text-foreground">
          Brak zdefiniowanych `work_templates`.
        </div>
      ) : (
        <div className="space-y-6">
          {filteredGroupedTemplates.map(([category, rows]) => (
            <section key={category} className="rates-accordion">
              {(() => {
                const isCollapsed = collapsedCategories[category] !== false;
                const toggle = () =>
                  setCollapsedCategories((prev) => ({
                    ...prev,
                    [category]: collapsedCategories[category] !== false ? false : true,
                  }));

                return (
                  <>
                    <button
                      type="button"
                      className="rates-accordion-trigger"
                      onClick={toggle}
                      aria-expanded={!isCollapsed}
                      aria-controls={`category-${encodeURIComponent(category)}`}
                    >
                      <span>{category}</span>
                      <TablerIcon name="chevron-down" size={18} />
                    </button>

                    {!isCollapsed && (
                      <div
                        id={`category-${encodeURIComponent(category)}`}
                        className="divide-y divide-[var(--line)] border-t border-[var(--line)]"
                      >
                        {rows.map((template) => {
                          const rowValue = values[template.id] ?? { labor: "", material: "" };
                          const rowSaving = Boolean(savingRows[template.id]);
                          const rowDirty = Boolean(dirtyRows[template.id]);
                          const laborPlaceholder = formatMoneyInput(
                            Number(template.default_labor_cost ?? 0),
                          );
                          const materialPlaceholder = formatMoneyInput(
                            Number(template.default_material_cost ?? 0),
                          );

                          return (
                            <div
                              key={template.id}
                              className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(220px,1fr)_180px_180px_120px] md:items-end"
                            >
                              <div>
                                <p className="text-sm font-semibold text-foreground">{template.name}</p>
                                <p className="text-xs text-foreground">
                                  Jednostka: {template.unit}
                                </p>
                              </div>

                              <div className="space-y-1">
                                <label
                                  className="text-xs text-foreground"
                                  htmlFor={`labor-${template.id}`}
                                >
                                  Robocizna / jedn.
                                </label>
                                <Input
                                  id={`labor-${template.id}`}
                                  className="text-foreground"
                                  inputMode="decimal"
                                  value={rowValue.labor}
                                  placeholder={laborPlaceholder}
                                  onChange={(e) => setFieldValue(template.id, "labor", e.target.value)}
                                />
                              </div>

                              <div className="space-y-1">
                                <label
                                  className="text-xs text-foreground"
                                  htmlFor={`material-${template.id}`}
                                >
                                  Materiał / jedn.
                                </label>
                                <Input
                                  id={`material-${template.id}`}
                                  className="text-foreground"
                                  inputMode="decimal"
                                  value={rowValue.material}
                                  placeholder={materialPlaceholder}
                                  onChange={(e) => setFieldValue(template.id, "material", e.target.value)}
                                />
                              </div>

                              <Button
                                type="button"
                                variant="secondary"
                                disabled={!rowDirty || rowSaving}
                                onClick={() => void saveTemplateRate(template.id)}
                              >
                                {rowSaving ? "Zapis..." : "Zapisz"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </section>
          ))}

          <section className="rounded-xl border border-border/70 bg-card text-foreground">
            <div className="border-b border-border/60 px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">Własne pozycje</h2>
              <p className="mt-1 text-xs text-foreground">
                Dodaj własną pozycję i ustaw stawki początkowe.
              </p>
            </div>

            <div className="grid gap-3 border-b border-border/60 px-4 py-3 md:grid-cols-[minmax(220px,1fr)_130px_160px_160px_120px] md:items-end">
              <div className="space-y-1">
                <label className="text-xs text-foreground">Nazwa</label>
                <Input
                  className="text-foreground"
                  value={customForm.name}
                  placeholder="np. Montaż lampy"
                  onChange={(e) => setCustomForm((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-foreground">Jednostka</label>
                <Select
                  value={customForm.unit}
                  onValueChange={(value) =>
                    setCustomForm((p) => ({ ...p, unit: value as CustomUnit }))
                  }
                >
                  <SelectTrigger className="text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOM_UNITS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-foreground">Robocizna / jedn.</label>
                <Input
                  className="text-foreground"
                  inputMode="decimal"
                  value={customForm.labor}
                  onChange={(e) => setCustomForm((p) => ({ ...p, labor: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-foreground">Materiał / jedn.</label>
                <Input
                  className="text-foreground"
                  inputMode="decimal"
                  value={customForm.material}
                  onChange={(e) => setCustomForm((p) => ({ ...p, material: e.target.value }))}
                />
              </div>
              <Button type="button" onClick={() => void handleCreateCustomPosition()} disabled={creatingCustom}>
                {creatingCustom ? "Dodawanie..." : "Dodaj"}
              </Button>
            </div>

            {sortedCustomTemplates.length === 0 ? (
              <div className="px-4 py-4 text-sm text-foreground">
                Brak własnych pozycji.
              </div>
            ) : (
              <div className="divide-y divide-border/60 text-foreground">
                {sortedCustomTemplates.map((template) => {
                  const rowValue = values[template.id] ?? { labor: "", material: "" };
                  const rowSaving = Boolean(savingRows[template.id]);
                  const rowDirty = Boolean(dirtyRows[template.id]);
                  return (
                    <div
                      key={template.id}
                      className="flex min-w-0 flex-col gap-3 px-4 py-3"
                    >
                      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <p className="min-w-0 break-words text-sm font-semibold text-foreground">
                          {template.name}
                        </p>
                        <p className="shrink-0 text-xs text-foreground">Jednostka: {template.unit}</p>
                      </div>

                      <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
                        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row">
                          <div className="min-w-0 flex-1 space-y-1">
                            <label className="text-xs text-foreground" htmlFor={`labor-${template.id}`}>
                              Robocizna / jedn.
                            </label>
                            <Input
                              id={`labor-${template.id}`}
                              className="text-foreground"
                              inputMode="decimal"
                              value={rowValue.labor}
                              placeholder={formatMoneyInput(Number(template.default_labor_cost ?? 0))}
                              onChange={(e) => setFieldValue(template.id, "labor", e.target.value)}
                            />
                          </div>
                          <div className="min-w-0 flex-1 space-y-1">
                            <label className="text-xs text-foreground" htmlFor={`material-${template.id}`}>
                              Materiał / jedn.
                            </label>
                            <Input
                              id={`material-${template.id}`}
                              className="text-foreground"
                              inputMode="decimal"
                              value={rowValue.material}
                              placeholder={formatMoneyInput(Number(template.default_material_cost ?? 0))}
                              onChange={(e) => setFieldValue(template.id, "material", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2 lg:ml-auto">
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={!rowDirty || rowSaving}
                            onClick={() => void saveTemplateRate(template.id)}
                          >
                            {rowSaving ? "Zapis..." : "Zapisz"}
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            disabled={deletingCustomId === template.id}
                            onClick={() => void handleDeleteCustomPosition(template.id)}
                          >
                            {deletingCustomId === template.id ? "Usuwanie..." : "Usuń"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </main>
    </div>
  );
}

